import router from '@/router'
import axios from 'axios'
import moment from 'moment'
import Vue from 'vue'
import Notifications from 'vue-notification'

Vue.use(Notifications)

const UserService = {
  biteMasterUrl () {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      return 'http://efish-master-www-php.dev.aimitservices.com/'
    } else {
      return 'https://bitecenter.com/'
    }
  },

  async verifyUser () {
    axios.defaults.baseURL = UserService.baseUrl()
    axios.defaults.headers.common.Authorization = `Bearer ${UserService.getToken()}`
    const user = this.getUser()

    if (!user.id) {
      return
    }
    const data = {
      userId: user.id
    }

    return axios({
      url: this.baseUrl() + 'api/v1/user/verify',
      method: 'post',
      data: data,
      headers: {
        Authorization: 'Bearer ' + this.getToken()
      }
    })
  },

  setLocalStorages (user, date = new Date()) {
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('lastEntry', JSON.stringify(date))
  },

  needToRefreshAuthData () {
    const lastEnty = JSON.parse(localStorage.getItem('lastEntry'))
    const curt = new Date()
    if (!lastEnty) {
      return true
    }

    if (moment(curt).diff(moment(lastEnty), 'hours') >= 12) {
      return true
    }

    return false
  },

  /**
   * Refreshes the efishcompany data
   * and the efish user data, this also
   * verifies if the account is still valid
   */
  async refreshAuthData () {
    // first refresh efish company info
    const company = this.getBaseCompany()

    if (!company || !company.id) {
      this.redirectToLogin('Invalid eFish company, please log in again.') // no company found, need to re-login
    }
    try {
      await this.setupBaseCompany(company.id)
      // TODO check for suspended or other maintenance updates
    } catch (err) {
      // invalid company ID, purge and redirect to login
      this.redirectToLogin('Invalid eFish company, please log in again.') // no company found, need to re-login
      return false
    }
    const userResponse = await this.verifyUser()
    if (userResponse) {
      this.setLocalStorages(userResponse.data)
    } else {
      this.redirectToLogin('Your session has expired, please login again.')
      return false
    }
  },

  async authHeader () {
    const user = this.getToken()
    // if no user storage, return null
    if (!user) {
      this.redirectToLogin('Your session has expired. Please login again.')
      return
    }
    // check if we need to refresh user token and company token
    // if true, refresh them
    if (this.needToRefreshAuthData()) {
      const result = await this.refreshAuthData()
      if (result === false) {
        return false
      }
    }

    // return authorization header with jwt token
    return {
      Authorization: 'Bearer ' + user
    }
  },

  getToken () {
    const user = this.getUserObject()
    if (user === null) {
      this.logout()
      return null
    } else {
      return user.token
    }
  },

  async setupBaseCompanyByCompanyUrl (companyUrl) {
    const response = await fetch(this.biteMasterUrl() + 'api/v1/company/url/' + encodeURIComponent(this.removeUrlPrefix(companyUrl)), {
      method: 'GET',
      headers: {
        Authorization: 'Bearer 12345'
      }
    })
    // url verified, use standard end point to retrieve necessary information
    if (response.ok) {
      const json = await response.json()
      return this.setupBaseCompany(json.id)
    } else {
      return Promise.reject(new Error('Invalid eFish Client ID'))
    }
  },

  async setupBaseCompany (companyId) {
    const response = await fetch(this.biteMasterUrl() + 'api/v1/company/' + companyId + '/efishdata', {
      method: 'GET',
      headers: {
        Authorization: 'Bearer 12345'
      }
    })

    if (response.ok) {
      const json = await response.json()
      localStorage.setItem('efishcompany', JSON.stringify(json))
      return json
    } else {
      return Promise.reject(new Error('Invalid eFish Client ID'))
    }
  },
  getConfigOptions () {
    const base = JSON.parse(localStorage.getItem('efishcompany'))
    if (base !== null && typeof base.configuration_options !== 'undefined') {
      return base.configuration_options
    } else {
      // bad company info
      this.logout()
    }
  },
  getConfigOption (name) {
    const options = this.getConfigOptions()
    let ret = {}
    options.forEach(x => {
      if (x.name === name) {
        ret = x
      }
    })
    return ret
  },
  // because can not put http:// into url in get function, so remove it, then put it back in the future
  removeUrlPrefix (url) {
    return url.replace(/^http(s?):\/\//i, '').replace(/\/$/, '')
  },

  getBaseCompany () {
    const base = JSON.parse(localStorage.getItem('efishcompany'))
    if (base !== null && typeof base.organization_info !== 'undefined') {
      return base.organization_info
    } else {
      this.logout()
      return false
    }
  },

  baseUrl () {
    // return authorization header with jwt token
    const user = this.getBaseCompany()
    if (user && user.url) {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        return 'http://efish-client-www-php.dev.aimitservices.com/'
      } else {
        // ensure there's a trailing slash...
        if (user.url.substring(-1) !== '/') {
          return user.url + '/'
        } else {
          return user.url
        }
      }
    } else {
      return false
    }
  },

  async login (username, password) {
    return fetch(this.baseUrl() + 'api/v1/user/authenticate', {
      method: 'POST',
      body: JSON.stringify({
        username: username,
        password: password
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(response => {
      return this.handleResponse(response)
    })
      .then(user => {
        // login successful if there's a jwt token in the response
        if (user.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          this.setLocalStorages(user)
        }
        return user
      })
  },

  logout () {
    // remove user from local storage to log user out
    localStorage.clear()
    // localStorage.removeItem('user');
  },

  loggedIn () {
    const user = JSON.parse(localStorage.getItem('user'))
    if (user === null) {
      return false
    } else if (typeof user.token === 'undefined') {
      this.logout()
    } else {
      return true // todo verify token and expiration stuff
    }
  },

  getUserObject () {
    const temp = localStorage.getItem('user')
    if (temp !== null) {
      return JSON.parse(localStorage.getItem('user'))
    } else {
      return null
    }
  },

  getUser () {
    const temp = this.getUserObject()
    if (temp !== null) {
      return temp.userinfo
    } else {
      return null
    }
  },
  getLocationById (id) {
    const locs = this.getLocations()
    let ret = {}
    locs.forEach(x => {
      if ((x.id * 1) /* stupid js */ === (id * 1)) {
        ret = x
      }
    })
    return ret
  },
  /**
   * Get all user locations
   */
  getLocations () {
    return JSON.parse(localStorage.getItem('user')).locations
  },
  /**
   * Get all locations with a specific permission tied to them.
   * @param {string} permission
   */
  getLocationsWithPermission (permission) {
    const locations = JSON.parse(localStorage.getItem('user')).locations
    const ret = []
    locations.forEach(element => {
      element.permissions.forEach(p => {
        if (p.name === permission) {
          ret.push(element)
        }
      })
    })
    return ret
  },
  hasLocationPermission (locationId, permission) {
    const locs = this.getLocationsWithPermission(permission)
    let hasit = false
    locs.forEach(x => {
      if (x.id === locationId) {
        hasit = true
      }
    })
    return hasit
  },
  getCompanies () {
    return JSON.parse(localStorage.getItem('user')).companies
  },
  getBrands () {
    return JSON.parse(localStorage.getItem('user')).brands
  },
  getDistricts () {
    return JSON.parse(localStorage.getItem('user')).districts
  },

  handleResponse (response) {
    const self = this
    return response.text().then(text => {
      const data = text && JSON.parse(text)
      if (!response.ok) {
        if (response.status === 401) {
          // auto logout if 401 response returned from api
          self.logout()
          // location.reload(true);
        }

        const error = (data && data.error) || response.statusText
        return Promise.reject(error)
      }

      return Promise.resolve(data)
    })
  },

  redirectToLogin (message) {
    this.logout()
    router.push('/login', () => {
      Vue.notify({
        group: 'notices',
        title: 'Logout Complete',
        text: message,
        type: 'danger'
      })
    })
  }
}

export default UserService
